import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Fade,
} from '@mui/material';

// Icons
import TrafficIcon from '@mui/icons-material/Traffic';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import VideocamIcon from '@mui/icons-material/Videocam';

import VideoStreamDialog from '../cards/VideoStreamDialog';
import { getCameraByCheckpost } from '../../utils/cameraService';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SEVERITY_COLOR = (mins) => (mins >= 3 ? '#E74C3C' : mins >= 2 ? '#F39C12' : '#F1C40F');

const LiveTimer = ({ minutesOpen, secondsOpen }) => {
  const secs = secondsOpen % 60;
  return (
    <Typography
      sx={{
        color: SEVERITY_COLOR(minutesOpen),
        fontWeight: 700,
        fontFamily: 'monospace',
        fontSize: '0.78rem',
        letterSpacing: '0.5px',
      }}
    >
      {minutesOpen}m {secs}s
    </Typography>
  );
};

// A single boom-barrier row — same compact look as an EventItem
const BoomRow = ({ alert, index, onClick }) => {
  const severityColor =
    alert.severity === 'High' ? '#E74C3C' : alert.severity === 'Moderate' ? '#F39C12' : '#F1C40F';

  return (
    <Box
      onClick={() => onClick(alert)}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        background: `linear-gradient(90deg, ${severityColor}12 0%, rgba(20,28,40,0.45) 100%)`,
        border: `1px solid ${severityColor}30`,
        borderRadius: '6px',
        px: 1.25,
        py: 0.85,
        mb: 0.75,
        ml: '18px',
        transition: 'transform 0.18s ease, background 0.18s ease, box-shadow 0.18s ease',
        '&:hover': {
          background: `linear-gradient(90deg, ${severityColor}22 0%, rgba(40,52,68,0.7) 100%)`,
          transform: 'translateX(3px)',
          boxShadow: `0 3px 10px ${severityColor}33`,
        },
      }}
    >
      {/* Left: dot + text */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            flexShrink: 0,
            backgroundColor: severityColor,
            boxShadow: `0 0 5px ${severityColor}`,
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ color: 'white', fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2 }}
          >
            {alert.areaName}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: '#9ca3af', fontSize: '0.68rem', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {alert.unitName} · {alert.wbName} · BB&nbsp;{alert.bbId}
          </Typography>
        </Box>
        <Chip
          label={alert.severity}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.58rem',
            fontWeight: 700,
            ml: 0.5,
            flexShrink: 0,
            backgroundColor: severityColor,
            color: 'white',
          }}
        />
        <Chip
          label={alert.wbCode}
          size="small"
          sx={{ height: 18, fontSize: '0.58rem', flexShrink: 0, backgroundColor: '#2c3e50', color: '#9ca3af' }}
        />
      </Box>

      {/* Right: timer + camera hint */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <AccessTimeIcon sx={{ color: '#9ca3af', fontSize: '0.8rem' }} />
          <LiveTimer minutesOpen={alert.minutesOpen} secondsOpen={alert.secondsOpen} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, color: '#1ABC9C' }}>
          <VideocamIcon sx={{ fontSize: '0.85rem' }} />
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 600 }}>Live</Typography>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * BoomBarrierPanel
 *
 * splitView={true}  → inline Card, matching DetectionCard's expanded layout
 * splitView={false} → kept as standalone (legacy; not used on main dashboard)
 */
const BoomBarrierPanel = ({ boomBarrierAlerts = [], onClose }) => {
  const [streamEvent, setStreamEvent] = useState(null);
  const [streamOpen, setStreamOpen] = useState(false);
  const [noCamera, setNoCamera] = useState(false);

  const sortedAlerts = useMemo(() => {
    const checkposts = boomBarrierAlerts.filter(
      (a) => a.wbCode && a.wbCode.toString().startsWith('CP')
    );
    const order = { High: 3, Moderate: 2, Low: 1 };
    return [...checkposts].sort((a, b) => {
      const diff = (order[b.severity] || 0) - (order[a.severity] || 0);
      return diff !== 0 ? diff : (b.minutesOpen || 0) - (a.minutesOpen || 0);
    });
  }, [boomBarrierAlerts]);

  const openStreamFor = (alert) => {
    const camera = getCameraByCheckpost(alert.wbName, alert.areaName, alert.wbCode);
    if (!camera) {
      setNoCamera(true);
      setTimeout(() => setNoCamera(false), 3500);
      return;
    }
    setStreamEvent({
      cameraIp: camera.ip,
      groupId: camera.groupId,
      location: `${alert.wbName} (BB #${alert.bbId})`,
      area: alert.areaName,
      eventType: 'Checkpost Camera',
      severity: alert.severity,
    });
    setStreamOpen(true);
  };

  const accentColor = sortedAlerts.some(a => a.severity === 'High')
    ? '#E74C3C'
    : sortedAlerts.some(a => a.severity === 'Moderate')
    ? '#F39C12'
    : '#E67E22';

  return (
    <>
      <Card
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(180deg, ${accentColor}14 0%, rgba(22,32,45,0.96) 28%, rgba(20,28,40,0.98) 100%)`,
          color: 'white',
          borderRadius: '14px',
          border: `1.5px solid ${accentColor}55`,
          boxShadow: `0 10px 32px ${accentColor}28, inset 0 1px 0 rgba(255,255,255,0.04)`,
          display: 'flex',
          flexDirection: 'column',
          // Top accent ribbon — matches DetectionCard splitView style
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${accentColor} 0%, ${accentColor}99 50%, transparent 100%)`,
            boxShadow: `0 0 12px ${accentColor}aa`,
            zIndex: 2,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: -40, left: -40,
            width: 280, height: 180,
            background: `radial-gradient(ellipse at top left, ${accentColor}22 0%, transparent 70%)`,
            pointerEvents: 'none',
            zIndex: 0,
          },
        }}
      >
        {/* ── Header ── */}
        <CardHeader
          avatar={
            <TrafficIcon
              sx={{
                color: accentColor,
                fontSize: '1.6rem',
                filter: `drop-shadow(0 0 8px ${accentColor}66)`,
              }}
            />
          }
          title={
            <Typography
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: '1.15rem',
                letterSpacing: '0.02em',
                textShadow: `0 0 18px ${accentColor}40`,
              }}
            >
              Open Boom Barriers
            </Typography>
          }
          action={
            onClose && (
              <IconButton
                onClick={(e) => { e.stopPropagation(); onClose(); }}
                size="small"
                sx={{
                  color: '#aaa',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  mr: 0.5,
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)', color: '#fff' },
                }}
              >
                <CloseIcon sx={{ fontSize: '1.1rem' }} />
              </IconButton>
            )
          }
          sx={{
            padding: '16px 18px 10px',
            position: 'relative',
            zIndex: 1,
            '& .MuiCardHeader-avatar': { marginRight: '14px' },
          }}
        />

        {/* ── Count row ── */}
        <Box sx={{ px: 2.25, pb: 1, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 6, height: 6, borderRadius: '50%',
                backgroundColor: accentColor,
                boxShadow: `0 0 8px ${accentColor}`,
                animation: 'liveCountDot 1.6s ease-in-out infinite',
                '@keyframes liveCountDot': {
                  '0%,100%': { opacity: 1 },
                  '50%': { opacity: 0.35 },
                },
              }}
            />
            <Typography
              variant="body2"
              sx={{ color: accentColor, fontSize: '0.78rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}
            >
              {sortedAlerts.length} checkpost{sortedAlerts.length !== 1 ? 's' : ''} currently open
            </Typography>
          </Box>
          <Divider sx={{ bgcolor: `${accentColor}30`, my: 1, height: '1.5px' }} />
        </Box>

        {/* ── Content ── */}
        <CardContent
          sx={{
            flexGrow: 1,
            padding: '0 18px 18px 18px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {noCamera && (
            <Fade in={noCamera}>
              <Box
                sx={{
                  mb: 1.5,
                  p: 1.25,
                  borderRadius: 1,
                  backgroundColor: 'rgba(231,76,60,0.12)',
                  border: '1px solid rgba(231,76,60,0.35)',
                  color: '#E74C3C',
                  fontSize: '0.8rem',
                }}
              >
                No matching checkpost camera found for that barrier.
              </Box>
            </Fade>
          )}

          <Box
            sx={{
              flexGrow: 1,
              maxHeight: '45vh',
              overflowY: 'auto',
              paddingRight: '8px',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-track': { background: '#17212b' },
              '&::-webkit-scrollbar-thumb': { background: '#2a3f55', borderRadius: '4px' },
            }}
          >
            {sortedAlerts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5, color: '#6b7280' }}>
                <CheckCircleIcon sx={{ fontSize: '2.8rem', mb: 1.5, color: '#1ABC9C' }} />
                <Typography sx={{ fontWeight: 600, color: '#1ABC9C', mb: 0.5 }}>
                  All Booms Closed
                </Typography>
                <Typography variant="caption" sx={{ color: '#6b7280' }}>
                  No active checkpost alerts right now
                </Typography>
              </Box>
            ) : (
              sortedAlerts.map((alert, i) => (
                <BoomRow key={alert.id} alert={alert} index={i} onClick={openStreamFor} />
              ))
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Live stream */}
      {streamOpen && streamEvent && (
        <VideoStreamDialog open={streamOpen} onClose={() => setStreamOpen(false)} event={streamEvent} />
      )}
    </>
  );
};

export default BoomBarrierPanel;
